import { Component, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { fadeInOut } from '@shared/skeleton-loader/animation-serivce/app.animation';
import { SearchService } from '@core/services/search.service';
import { Router } from '@angular/router';
import { ToastService } from '@core/services/toast.service';
import { AuthService } from '@core/services/auth-new.service';
import { CommonService } from '@shared/services/common.service';
import { DropdownOption } from '@shared/components/common-components/custom-dropdown';
import { DateRange } from '@shared/components/date-range-picker/date-range-picker.component';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
  animations: [fadeInOut]
})
export class AdvancedSearchComponent implements OnInit {
  @Output() closeAdvancedSearch = new EventEmitter<void>();

  advancedSearchForm: FormGroup;
  searchResults: any[] = [];
  isLoading: boolean = false;
  showResults: boolean = false;
  noResultFound: boolean = false;

  // Dropdown options (original data)
  customerStatus: any[] = [];
  orderType: any[] = [];
  accountType: any[] = [];
  stateOptions: any[] = [];
  isHiddenData: any[] = [];

  // Dropdown options for custom dropdown component
  customerStatusOptions: DropdownOption[] = [];
  orderTypeOptions: DropdownOption[] = [];
  accountTypeOptions: DropdownOption[] = [];
  stateDropdownOptions: DropdownOption[] = [];

  // String arrays for common-dropdown
  customerStatusItems: string[] = [];
  orderTypeItems: string[] = [];
  accountTypeItems: string[] = [];
  stateItems: string[] = [];

  // Selected items for common-dropdown
  selectedCustomerStatus: string[] = [];
  selectedOrderType: string[] = [];
  selectedAccountType: string[] = [];
  selectedState: string[] = [];

  // Date picker properties
  customDateRange: Date[] | null = null;
  showCalendar = false;

  // Prefill loading state — true while dropdown data is loading after edit icon click
  isPrefilling = false;
  today: Date = new Date();
  yearRange: string = '2000:2030';

  private fb = inject(FormBuilder);
  private searchService = inject(SearchService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);

  constructor() {
    this.advancedSearchForm = this.fb.group({
      firstName: [null, [Validators.pattern('^[a-zA-Z]+$')]],
      lastName: [null, [Validators.pattern('^[a-zA-Z]+$')]],
      fullName: [null, [Validators.pattern('^[a-zA-Z ]+$')]],
      alternateContactNumber: [null, [Validators.pattern('^[0-9]{10}$')]],
      address: [null],
      zipCode: [null, [Validators.pattern('^[0-9]{5,6}$')]],
      states: [null, [Validators.pattern('^[a-zA-Z ]+$')]],
      CustomerStatusID: [null],
      OrderTypeID: [null],
      PlanTypeID: [null],
      nladApplicationId: [null, [Validators.pattern('^[0-9]+$')]],
      nvSubscriberId: [null, [Validators.pattern('^[0-9]+$')]],
      fromDate: [null],
      toDate: [null],
      country: [1]
    });
  }

  ngOnInit(): void {
    let capturedPrefillData: any = null;

    // BehaviorSubject fires synchronously — capture and immediately clear so stale
    // .then() callbacks from a previous component instance cannot consume this data
    this.searchService.prefillRequest$.pipe(take(1)).subscribe((prefillData: any) => {
      if (prefillData) {
        capturedPrefillData = prefillData;
        this.isPrefilling = true;
        this.searchService.setPrefillRequest(null); // clear immediately, before any async work
      }
    });

    this.loadDropdownData().then(() => {
      if (capturedPrefillData) {
        this.prefillForm(capturedPrefillData);
      }
      this.isPrefilling = false;
    });
    this.getSideMenuSettings();
  }

  prefillForm(data: any): void {
    this.advancedSearchForm.patchValue({
      fullName: data.fullName || null,
      alternateContactNumber: data.alternateContactNumber || null,
      address: data.address || null,
      zipCode: data.zipCode || null
    });

    if (data.selectedCustomerStatus?.length > 0) {
      this.selectedCustomerStatus = data.selectedCustomerStatus;
      const ids = this.customerStatus.filter((s: any) => data.selectedCustomerStatus.includes(s.Name)).map((s: any) => s.id);
      if (ids.length > 0) this.advancedSearchForm.patchValue({ CustomerStatusID: ids });
    }
    if (data.selectedOrderType?.length > 0) {
      this.selectedOrderType = data.selectedOrderType;
      const ids = this.orderType.filter((o: any) => data.selectedOrderType.includes(o.Description)).map((o: any) => o.id);
      if (ids.length > 0) this.advancedSearchForm.patchValue({ OrderTypeID: ids });
    }
    if (data.selectedAccountType?.length > 0) {
      this.selectedAccountType = data.selectedAccountType;
      const ids = this.accountType.filter((a: any) => data.selectedAccountType.includes(a.Name)).map((a: any) => a.id);
      if (ids.length > 0) this.advancedSearchForm.patchValue({ PlanTypeID: ids });
    }
    if (data.selectedState?.length > 0) {
      this.selectedState = data.selectedState;
      const codes = this.stateOptions.filter((s: any) => data.selectedState.includes(s.StateName)).map((s: any) => s.StateCode);
      if (codes.length > 0) this.advancedSearchForm.patchValue({ states: codes });
    }
    if (data.customDateRange?.length === 2) {
      this.customDateRange = [new Date(data.customDateRange[0]), new Date(data.customDateRange[1])];
      this.advancedSearchForm.patchValue({
        fromDate: this.formatDateForForm(this.customDateRange[0]),
        toDate: this.formatDateForForm(this.customDateRange[1])
      });
    }
  }

  async getSideMenuSettings(): Promise<void> {
    try {
      // this.commonService.sideMenuInitilize();
      // this.commonService.getSideMenuBar().subscribe((SETTINGSItems: any) => {
      //   this.isHiddenData = SETTINGSItems || [];
      //   console.log("Advance Search Settings:", this.isHiddenData);
      // });
    } catch (error) {
      console.error('Error loading side menu settings:', error);
    }
  }

  async loadDropdownData(): Promise<void> {
    try {
      // Load customer statuses
      const statusResponse = await this.searchService.getCustomerStatus();
      if (statusResponse?.Status) {
        this.customerStatus = statusResponse.data || [];
        this.customerStatusOptions = this.customerStatus.map(status => ({
          value: status.id,
          label: status.Name
        }));
        this.customerStatusItems = this.customerStatus.map(status => status.Name);
      }

      // Load order types
      const orderTypesResponse = await this.searchService.getOrderType();
      if (orderTypesResponse?.Status) {
        this.orderType = orderTypesResponse.data || [];
        this.orderTypeOptions = this.orderType.map(order => ({
          value: order.id,
          label: order.Description
        }));
        this.orderTypeItems = this.orderType.map(order => order.Description);
      }

      // Load account types (plan types)
      const accountTypesResponse = await this.searchService.getAccountType();
      if (accountTypesResponse?.Status) {
        this.accountType = accountTypesResponse.data || [];
        this.accountTypeOptions = this.accountType.map(account => ({
          value: account.id,
          label: account.Name
        }));
        this.accountTypeItems = this.accountType.map(account => account.Name);
      }

      // Load states
      await this.getStates();
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  }

  async getStates(): Promise<void> {
    try {
      const data = { CountryID: 1 };
      this.commonService.getStates(data).subscribe({
        next: (res: any) => {
          if (res?.Status) {
            this.stateOptions = res.data || [];
            this.stateDropdownOptions = this.stateOptions.map(state => ({
              value: state.StateCode,
              label: state.StateName
            }));
            this.stateItems = this.stateOptions.map(state => state.StateName);
            console.log('States loaded:', this.stateOptions.length);
          } else {
            console.warn('Failed to load states:', res?.message);
          }
        },
        error: (err) => {
          console.error('Error loading states:', err);
        }
      });
    } catch (err) {
      console.error('Error in getStates:', err);
    }
  }

  allowOnlyAlphabets(event: KeyboardEvent): void {
    const charCode = event.key;
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

    // Allow alphabets and space
    if (!/^[a-zA-Z ]$/.test(charCode) && !allowedKeys.includes(charCode)) {
      event.preventDefault();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.key;
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

    // Allow only numeric characters
    if (!/^[0-9]$/.test(charCode) && !allowedKeys.includes(charCode)) {
      event.preventDefault();
    }
  }

  async onSearch(): Promise<void> {
    let formValue = this.advancedSearchForm.value;

    // Remove null or blank values
    formValue = this.removeNullOrBlank(formValue);

    // Check if at least one field is filled
    if (Object.keys(formValue).length === 0 || (Object.keys(formValue).length === 1 && formValue.country === 1)) {
      this.toastService.warning('Search Required', 'Please enter at least one search criteria');
      return;
    }

    this.isLoading = true;
    this.noResultFound = false;
    this.showResults = false;

    try {
      // Prepare payload similar to V1
      const user = this.authService.user();
      const payload: any = {
        companyId: "UNITYCRM0021",
        userID: user?.userID,
        ...formValue,
        perPage: 10
      };

      // Handle states array
      if (payload.states) {
        payload['states[]'] = payload.states;
        delete payload.states;
      }

      // Replace CustomerStatusID (IDs) with status[] (Names array)
      delete payload.CustomerStatusID;
      if (this.selectedCustomerStatus.length > 0) {
        payload['status[]'] = this.selectedCustomerStatus;
      }

      // Replace OrderTypeID (IDs) with orderType[] (Codes array)
      delete payload.OrderTypeID;
      if (this.selectedOrderType.length > 0) {
        payload['orderType[]'] = this.orderType
          .filter(o => this.selectedOrderType.includes(o.Description))
          .map(o => o.Code);
      }

      // Replace PlanTypeID (IDs) with accountType[] (Names array)
      delete payload.PlanTypeID;
      if (this.selectedAccountType.length > 0) {
        payload['accountType[]'] = this.selectedAccountType;
      }

      // Remove fromDate and toDate from filtered data (as per V1)
      delete payload.fromDate;
      delete payload.toDate;
      delete payload.country;

      console.log('Advanced Search Payload:', payload);

      // Call the advance search API
      const response = await this.searchService.advanceOrderSearch(payload);

      if (response?.Status && response?.data) {
        this.searchResults = response.data?.data || [];
        this.showResults = true;
        this.noResultFound = this.searchResults.length === 0;

        if (this.searchResults.length > 0) {
          this.toastService.success('Search Complete', `Found ${this.searchResults.length} result(s)`);

          // Build human-readable label from selected values
          const rawForm = this.advancedSearchForm.value;
          const labelParts: string[] = [];
          if (rawForm.fullName) labelParts.push(`Name: ${rawForm.fullName}`);
          if (rawForm.alternateContactNumber) labelParts.push(`Phone: ${rawForm.alternateContactNumber}`);
          if (rawForm.address) labelParts.push(`Address: ${rawForm.address}`);
          if (rawForm.zipCode) labelParts.push(`Zip: ${rawForm.zipCode}`);
          if (this.selectedCustomerStatus.length > 0) labelParts.push(`Status: ${this.selectedCustomerStatus.join(', ')}`);
          if (this.selectedOrderType.length > 0) labelParts.push(`Order Type: ${this.selectedOrderType.join(', ')}`);
          if (this.selectedAccountType.length > 0) labelParts.push(`Account Type: ${this.selectedAccountType.join(', ')}`);
          if (this.selectedState.length > 0) labelParts.push(`State: ${this.selectedState.join(', ')}`);
          if (this.customDateRange?.length === 2) {
            labelParts.push(`Date: ${this.formatDateForDisplay(this.customDateRange[0])} - ${this.formatDateForDisplay(this.customDateRange[1])}`);
          }

          // Set payload FIRST so it's available when advanceSearchedData$ subscription fires
          this.searchService.setAdvanceSearchedPayloadData({
            ...payload,
            _displayLabel: labelParts.join(' | ') || 'Advanced Search',
            _prefillData: {
              fullName: rawForm.fullName || '',
              alternateContactNumber: rawForm.alternateContactNumber || '',
              address: rawForm.address || '',
              zipCode: rawForm.zipCode || '',
              selectedCustomerStatus: this.selectedCustomerStatus,
              selectedOrderType: this.selectedOrderType,
              selectedAccountType: this.selectedAccountType,
              selectedState: this.selectedState,
              customDateRange: this.customDateRange
                ? [this.customDateRange[0].toISOString(), this.customDateRange[1].toISOString()]
                : null
            }
          });
          // Set search data second — this triggers the subscription in search-result component
          this.searchService.setAdvanceSearchedData(response);

          // Navigate to search results page
          setTimeout(() => {
            this.router.navigate(['/search-result']);
            this.closeAdvancedSearch.emit();
          }, 500);
        } else {
          this.toastService.info('No Results', 'No results found matching your criteria');
        }
      } else {
        this.noResultFound = true;
        this.showResults = true;
        this.toastService.info('No Results', response?.message || 'No results found matching your criteria');
      }
    } catch (error: any) {
      console.error('Advanced search error:', error);
      this.toastService.error('Search Error', error?.error?.message || error?.message || 'An error occurred during search');
      this.noResultFound = true;
    } finally {
      this.isLoading = false;
    }
  }

  onDateRangeSelected(dateRange: DateRange): void {
    this.showCalendar = false;
    if (dateRange.startDate && dateRange.endDate) {
      this.customDateRange = [dateRange.startDate, dateRange.endDate];
      this.advancedSearchForm.patchValue({
        fromDate: this.formatDateForForm(dateRange.startDate),
        toDate: this.formatDateForForm(dateRange.endDate)
      });
    }
  }

  onReset(): void {
    this.advancedSearchForm.reset();
    this.advancedSearchForm.patchValue({ country: 1 });
    this.searchResults = [];
    this.showResults = false;
    this.noResultFound = false;
    // Reset dropdown selections
    this.selectedCustomerStatus = [];
    this.selectedOrderType = [];
    this.selectedAccountType = [];
    this.selectedState = [];
    this.customDateRange = null;
    this.showCalendar = false;
  }

  triggerClose(): void {
    this.closeAdvancedSearch.emit();
  }

  // Helper method to remove null or blank values
  private removeNullOrBlank(obj: any): any {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
  }

  // Check if NLAD field should be shown
  isNladVisible(): boolean {
    return this.isHiddenData[0]?.isHidden === 1;
  }

  // Check if NV Subscriber field should be shown
  isNvSubscriberVisible(): boolean {
    return this.isHiddenData[1]?.isHidden === 1;
  }

  // Common dropdown selection handlers
  onCustomerStatusSelect(items: string[]): void {
    this.selectedCustomerStatus = items;
    const ids = this.customerStatus.filter(s => items.includes(s.Name)).map(s => s.id);
    this.advancedSearchForm.patchValue({ CustomerStatusID: ids.length > 0 ? ids : null });
  }

  onOrderTypeSelect(items: string[]): void {
    this.selectedOrderType = items;
    const ids = this.orderType.filter(o => items.includes(o.Description)).map(o => o.id);
    this.advancedSearchForm.patchValue({ OrderTypeID: ids.length > 0 ? ids : null });
  }

  onAccountTypeSelect(items: string[]): void {
    this.selectedAccountType = items;
    const ids = this.accountType.filter(a => items.includes(a.Name)).map(a => a.id);
    this.advancedSearchForm.patchValue({ PlanTypeID: ids.length > 0 ? ids : null });
  }

  onStateSelect(items: string[]): void {
    this.selectedState = items;
    const codes = this.stateOptions.filter(s => items.includes(s.StateName)).map(s => s.StateCode);
    this.advancedSearchForm.patchValue({ states: codes.length > 0 ? codes : null });
  }

  // Date picker methods
  onCustomDateSelect(_event: any): void {
    // Handle custom date selection if needed
  }

  applyCustomDateRange(dateRange: Date[]): void {
    this.customDateRange = dateRange;
    this.showCalendar = false;

    // Update form controls with selected dates
    if (dateRange && dateRange.length === 2) {
      this.advancedSearchForm.patchValue({
        fromDate: this.formatDateForForm(dateRange[0]),
        toDate: this.formatDateForForm(dateRange[1])
      });
    }
  }

  cancelCustomDateRange(): void {
    this.showCalendar = false;
  }

  toggleCalendar(): void {
    this.showCalendar = !this.showCalendar;
  }

  private dateRangeBtnMouseInteraction = false;

  onDateRangeBtnMouseDown(): void {
    // Flag mouse interaction so focus handler doesn't auto-open on click
    this.dateRangeBtnMouseInteraction = true;
    setTimeout(() => { this.dateRangeBtnMouseInteraction = false; }, 0);
  }

  onDateRangeBtnFocus(): void {
    // Auto-open calendar only when focused via keyboard (Tab), not mouse click
    if (!this.dateRangeBtnMouseInteraction && !this.showCalendar) {
      this.showCalendar = true;
    }
  }

  onDateRangeBtnKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab' && this.showCalendar) {
      // Close calendar and let Tab move focus to the next element naturally
      this.showCalendar = false;
    }
  }

  formatDateForForm(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateForDisplay(date: Date): string {
    if (!date) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }
}

